'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, X, User } from 'lucide-react';
import type { Team } from '@/types/tournament.model';

interface MatchDeleteConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  isSubmitting: boolean;
  team1Name: string;
  team2Name: string;
  team1?: Team;
  team2?: Team;
}

const TeamDisplay = ({
  teamLabel,
  team,
  teamName,
}: {
  teamLabel: string;
  team?: Team;
  teamName: string;
}) => (
  <div>
    <p className="mb-2 text-xs font-medium uppercase text-gray-500">
      {teamLabel}
    </p>
    {team && team.players && team.players.length > 0 ? (
      <div className="space-y-1.5">
        {team.players.map((player, index) => (
          <div
            key={player.id || index}
            className="flex items-center gap-2 text-sm"
          >
            <User className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <div className="flex flex-col">
              <span className="font-semibold text-gray-900">{player.name}</span>
              <span className="text-xs text-gray-500">@{player.username}</span>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm font-semibold text-gray-900">{teamName}</p>
    )}
  </div>
);

export function MatchDeleteConfirmModal({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  team1Name,
  team2Name,
  team1,
  team2,
}: MatchDeleteConfirmModalProps) {
  const [confirmationText, setConfirmationText] = useState('');
  const REQUIRED_TEXT = 'Eliminar';

  const handleConfirm = async () => {
    if (confirmationText === REQUIRED_TEXT) {
      await onConfirm();
      setConfirmationText('');
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setConfirmationText('');
    onOpenChange(false);
  };

  const isConfirmDisabled = confirmationText !== REQUIRED_TEXT || isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-red-100 p-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <DialogTitle>Confirmar eliminación de partido</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-sm text-gray-700">
            Estás a punto de eliminar el siguiente partido:
          </p>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="space-y-3">
              <TeamDisplay
                teamLabel="Equipo 1"
                team={team1}
                teamName={team1Name}
              />
              <div className="my-2 border-t border-gray-300"></div>
              <TeamDisplay
                teamLabel="Equipo 2"
                team={team2}
                teamName={team2Name}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-amber-50 p-4">
            <p className="text-sm text-amber-800">
              <strong>Advertencia:</strong> Esta acción no se puede deshacer. El
              partido será eliminado permanentemente del torneo.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmation" className="text-sm font-medium">
              Para confirmar, escribe{' '}
              <span className="font-bold text-red-600">
                &quot;{REQUIRED_TEXT}&quot;
              </span>{' '}
              en el campo de abajo:
            </Label>
            <Input
              id="confirmation"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={REQUIRED_TEXT}
              disabled={isSubmitting}
              className="border-gray-300"
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            <AlertTriangle className="mr-1 h-3.5 w-3.5" />
            Eliminar partido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
